import {
  Invoice,
  INVOICE_TYPE_FUTURE,
  INVOICE_TYPE_ISSUED,
  InvoiceAndBenefits,
  IssuedInvoice,
} from '../types';
import { PayNowButton } from './PayNowButton';
import { getInvoiceRenderInfo, isInvoicePastDue, isInvoicePayable } from '../utils';
import { StyleVariables } from '../../commons/styleConstants';
import { usdFormatter } from '../../program/util';
import clsx from 'clsx';
import React, {
  ForwardedRef,
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { PayAllButton } from './PayAllButton';
import { findLastIndex } from 'lodash';
import { nonBreakableSpace } from '../../util/utils';
import { usePayAllFeatureSwitcher } from '../hooks/usePayAllFeatureSwitcher';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

const TopSegment = (props: {
  title: string;
  invoice: Invoice | null;
  price: number | null;
  referenceInvoice: Invoice | null;
  invoices: Invoice[] | null;
}) => {
  const { title, invoice, price, referenceInvoice, invoices } = props;
  const effectiveInvoice = invoice ?? referenceInvoice;
  const isSettled =
    price === null &&
    effectiveInvoice?.type === INVOICE_TYPE_ISSUED &&
    effectiveInvoice.status === 'PAID';
  return (
    <div
      className={clsx('timeline-top__segment-container', {
        'timeline-top__segment-container--narrow': invoices !== null,
      })}
      data-test="timeline-top__segment-container"
    >
      {invoice && isInvoicePayable(invoice) && (
        <>
          {isInvoicePastDue(invoice) ? (
            <div className="timeline-top__title--past-due" data-test="timeline-top__title">
              {title} past{nonBreakableSpace}due
            </div>
          ) : (
            <div className="timeline-top__title" data-test="timeline-top__title">
              {title} due
            </div>
          )}
          <PayNowButton
            invoice={invoice as IssuedInvoice}
            smallSize={true}
            source="timeline"
            dataTest="timeline-top__pay-now"
          />
        </>
      )}
      {invoice && invoice.type === INVOICE_TYPE_ISSUED && invoice.status === 'PAID' && (
        <>
          <div className="timeline-top__title--past" data-test="timeline-top__title">
            {title}
          </div>
          <div className="timeline-top__amount--paid" data-test="timeline-top__amount">
            {usdFormatter.format(invoice.amountDue)} paid
          </div>
        </>
      )}
      {(!invoice || invoice.type === INVOICE_TYPE_FUTURE) && !invoices && (
        <>
          <div
            className={isSettled ? 'timeline-top__title--past' : 'timeline-top__title'}
            data-test="timeline-top__title"
          >
            {title}
          </div>
          <div
            className={isSettled ? 'timeline-top__amount--settled' : 'timeline-top__amount'}
            data-test="timeline-top__amount"
          >
            {price
              ? usdFormatter.format(price)
              : invoice
              ? usdFormatter.format(invoice.amountDue)
              : 'Included'}
          </div>
        </>
      )}

      {invoices && (
        <>
          <div className="timeline-top__title" data-test="timeline-top__title">
            {title}
          </div>
          <PayAllButton
            invoices={invoices}
            smallSize={true}
            source="timeline"
            dataTest="timeline-top-line__pay-all"
          />
        </>
      )}
      {invoice && invoice.type === INVOICE_TYPE_FUTURE && (
        <div
          className="timeline-top__future-invoice-date"
          data-test="timeline-top__future-invoice-date"
        >
          Opens on {moment(invoice.periodStart).format('MMM D')}
        </div>
      )}
    </div>
  );
};

const TopLine = (props: {
  segmentsPerYear: number;
  segments: Segment[];
  historicInvoices: Invoice[];
}) => {
  const { segmentsPerYear, segments, historicInvoices } = props;
  return (
    <div className="timeline-top__container">
      {segments.map((segment, i) => {
        switch (segment.type) {
          case 'tail':
            return null;
          case 'billing-cycle':
            const year = Math.floor(segment.index / segmentsPerYear) + 1;
            const monthNumber = (segment.index % segmentsPerYear) + 1;
            const title =
              year === 1
                ? monthNumber !== segmentsPerYear || segmentsPerYear >= 12
                  ? `Month${nonBreakableSpace}${monthNumber}`
                  : `Months${nonBreakableSpace}${monthNumber}–12`
                : monthNumber !== segmentsPerYear || segmentsPerYear >= 12
                ? `Year${nonBreakableSpace}${year} month${nonBreakableSpace}${monthNumber}`
                : `Year${nonBreakableSpace}${year} months${nonBreakableSpace}${monthNumber}–12`;
            return (
              <TopSegment
                key={i}
                title={title}
                invoice={typeof segment.invoice === 'object' ? segment.invoice : null}
                price={typeof segment.invoice === 'number' ? segment.invoice : null}
                referenceInvoice={segment.referenceInvoice}
                invoices={null}
              />
            );
          case 'pay-all':
            return (
              <TopSegment
                title="Total due"
                invoice={null}
                price={null}
                referenceInvoice={null}
                invoices={[
                  ...historicInvoices,
                  ...(segments
                    .map((x) => (x.type === 'billing-cycle' ? x.invoice : null))
                    .filter((x) => x) as Invoice[]),
                ]}
              />
            );
          default:
            return <></>;
        }
      })}
    </div>
  );
};

const MiddleLineSegmentTick = (props: { color: string }) => {
  const { color } = props;
  return (
    <div
      className="timeline-middle-line__segment-tick"
      data-test="timeline-middle-line__segment-tick"
      style={{ backgroundColor: color }}
    />
  );
};

const MiddleLineSegmentBody = (props: { color: string; toColor: string; dashed: boolean }) => {
  const { color, toColor, dashed } = props;
  return (
    <div
      className={clsx('timeline-middle-line__segment-body', {
        'timeline-middle-line__segment-body--narrow': dashed,
      })}
      data-test="timeline-middle-line__segment-body"
      style={{
        background: dashed
          ? `linear-gradient(to right, transparent 50%, white 50%), linear-gradient(to right, ${color}, ${
              toColor ?? color
            })`
          : color,
        backgroundSize: '16px 2px, 100% 2px',
      }}
    />
  );
};

const MiddleLineSegment = forwardRef(
  (
    props: {
      isFirst: boolean;
      isDashed: boolean;
      isLast: boolean;
      invoice: Invoice | number | null;
      referenceInvoice: Invoice | null;
      hasPastDueInvoices: boolean;
      isPayAll: boolean;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { isFirst, isDashed, isLast, invoice, referenceInvoice, hasPastDueInvoices, isPayAll } =
      props;
    const color =
      isPayAll && hasPastDueInvoices
        ? StyleVariables.red500
        : isPayAll
        ? StyleVariables.cyan500
        : isDashed ||
          typeof invoice === 'number' ||
          invoice?.type === INVOICE_TYPE_FUTURE ||
          referenceInvoice?.type === INVOICE_TYPE_FUTURE ||
          (!invoice && !referenceInvoice)
        ? StyleVariables.neutral200
        : hasPastDueInvoices
        ? StyleVariables.red500
        : invoice !== null
        ? getInvoiceRenderInfo(invoice).lineColor
        : referenceInvoice !== null
        ? getInvoiceRenderInfo(referenceInvoice).lineColor
        : StyleVariables.neutral200;
    const toColor = isDashed ? StyleVariables.neutral200 : color;
    return (
      <div
        ref={ref}
        className={clsx('timeline-middle-line__segment', { 'align-self-start': isDashed })}
        data-test="timeline-middle-line__segment"
      >
        {isFirst && <MiddleLineSegmentTick color={color} />}
        <MiddleLineSegmentBody color={color} toColor={toColor} dashed={isDashed} />
        {!isLast && <MiddleLineSegmentTick color={toColor} />}
      </div>
    );
  }
);

const getScrollToIndex = (segments: Segment[]) => {
  const payAllIndex = segments.findIndex((x) => x.type === 'pay-all');
  if (payAllIndex >= 0) return payAllIndex;

  const unpaidInvoiceIndex = segments.findIndex((x) => {
    return (
      x.type === 'billing-cycle' &&
      (typeof x.invoice === 'number' ||
        (x.invoice?.type === INVOICE_TYPE_ISSUED &&
          (x.invoice.status === 'OPEN' || x.invoice.status === 'UNCOLLECTIBLE')))
    );
  });
  if (unpaidInvoiceIndex >= 0) return unpaidInvoiceIndex;

  return segments.length - 1;
};

const MiddleLine = (props: { segments: Segment[]; historicInvoices: Invoice[] }) => {
  const { segments, historicInvoices } = props;

  let hasPastDueInvoices = historicInvoices.some((x) => isInvoicePastDue(x));
  return (
    <div className="timeline-middle__container">
      {segments.map((segment, index) => {
        switch (segment.type) {
          case 'tail':
            return (
              <MiddleLineSegment
                isFirst={false}
                isLast={true}
                isDashed={true}
                invoice={null}
                referenceInvoice={null}
                hasPastDueInvoices={hasPastDueInvoices}
                isPayAll={false}
              />
            );
          case 'billing-cycle':
            const { invoice, referenceInvoice } = segment;
            if (
              !hasPastDueInvoices &&
              invoice &&
              typeof invoice === 'object' &&
              isInvoicePastDue(invoice)
            )
              hasPastDueInvoices = true;
            return (
              <MiddleLineSegment
                key={index}
                isFirst={index === 0}
                isLast={segments.length - 1 === index}
                isDashed={false}
                invoice={invoice}
                referenceInvoice={referenceInvoice}
                hasPastDueInvoices={hasPastDueInvoices}
                isPayAll={false}
              />
            );
          case 'pay-all':
            return (
              <MiddleLineSegment
                isFirst={index === 0}
                isDashed={true}
                isLast={index === segments.length - 1}
                invoice={null}
                referenceInvoice={null}
                hasPastDueInvoices={hasPastDueInvoices}
                isPayAll={true}
              />
            );
          default:
            return <></>;
        }
      })}
    </div>
  );
};

const BottomSegment = (props: {
  invoice: Invoice | null;
  referenceInvoice: Invoice | null;
  regularBenefits: string[];
  specialBenefits: string[];
  hasDueInvoices: boolean;
  isNarrow: boolean;
  beforeReactivationLocked: boolean;
}) => {
  const {
    invoice,
    referenceInvoice,
    regularBenefits,
    specialBenefits,
    hasDueInvoices,
    isNarrow,
    beforeReactivationLocked,
  } = props;
  const effectiveInvoice = invoice ?? referenceInvoice;
  const invoicePaid =
    effectiveInvoice?.type === INVOICE_TYPE_ISSUED &&
    effectiveInvoice.status === 'PAID' &&
    !hasDueInvoices;
  return (
    <div
      className={clsx('timeline-bottom__segment-container', {
        'timeline-bottom__segment-container--narrow': isNarrow,
      })}
      data-test="timeline-bottom__segment-container"
    >
      <div
        className={clsx('timeline-bottom__regular-benefits-container', {
          'timeline-bottom__regular-benefits-container--past': invoicePaid,
        })}
      >
        {regularBenefits.map((x, i) => (
          <Fragment key={`reg-benefit-container-${i}`}>
            <div key={`reg-benefit-${i}`} data-test="timeline-bottom__regular-benefit">
              {x}
            </div>
            {i < regularBenefits.length - 1 || specialBenefits.length > 0 ? (
              <div
                key={`reg-benefit-plus-${i}`}
                className={clsx('timeline-bottom__regular-benefit-plus', {
                  'timeline-bottom__regular-benefit-plus--past': invoicePaid,
                })}
              >
                +
              </div>
            ) : null}
          </Fragment>
        ))}
      </div>
      <div className="timeline-bottom__special-benefits-container">
        {specialBenefits.map((x, i) => (
          <SpecialBenefit
            benefit={x}
            key={i}
            isAvailable={invoicePaid && !hasDueInvoices && !beforeReactivationLocked}
            locked={beforeReactivationLocked}
          />
        ))}
      </div>
    </div>
  );
};

export const SpecialBenefit = (props: {
  benefit: string;
  isAvailable: boolean;
  locked: boolean;
}) => {
  const { benefit, isAvailable, locked } = props;
  const [tooltipId] = useState(`tooltip-id_${uuidv4()}`);

  return (
    <div
      className={clsx('timeline-bottom__special-benefit-container', {
        'timeline-bottom__special-benefit-container--available': isAvailable,
      })}
      data-test="timeline-bottom__special-benefit-container"
    >
      {locked && (
        <>
          <span
            className="icon-button__icon"
            data-tooltip-id={tooltipId}
            data-tooltip-place={'bottom'}
          >
            {`🔒${benefit}`}
          </span>
          <Tooltip
            id={tooltipId}
            className="info-button__tooltip"
            noArrow={true}
            place={'bottom'}
            positionStrategy={'absolute'}
          >
            <span>Unlocked after reactivation payment</span>
          </Tooltip>
        </>
      )}
      {!locked && <span>{benefit}</span>}
    </div>
  );
};

const BottomLine = (props: { segments: Segment[]; historicInvoices: Invoice[] }) => {
  const { segments, historicInvoices } = props;
  let hasDueInvoices = historicInvoices.some(isInvoicePayable);
  return (
    <div className="timeline-bottom__container">
      {segments.map((segment, index) => {
        if (segment.type === 'pay-all' || segment.type === 'tail')
          return (
            <BottomSegment
              invoice={null}
              referenceInvoice={null}
              regularBenefits={[]}
              specialBenefits={[]}
              hasDueInvoices={hasDueInvoices}
              isNarrow={true}
              beforeReactivationLocked={false}
            />
          );
        const {
          invoice,
          referenceInvoice,
          regularBenefits,
          specialBenefits,
          beforeReactivationLocked,
        } = segment;
        if (typeof invoice === 'object' && invoice && isInvoicePayable(invoice))
          hasDueInvoices = true;
        return (
          <BottomSegment
            key={index}
            invoice={
              typeof invoice === 'object' && invoice?.type === INVOICE_TYPE_ISSUED ? invoice : null
            }
            referenceInvoice={referenceInvoice}
            regularBenefits={regularBenefits}
            specialBenefits={specialBenefits}
            hasDueInvoices={hasDueInvoices}
            isNarrow={false}
            beforeReactivationLocked={beforeReactivationLocked}
          />
        );
      })}
    </div>
  );
};

type Segment =
  | ({
      type: 'billing-cycle';
      index: number;
    } & InvoiceAndBenefits)
  | {
      type: 'pay-all';
    }
  | {
      type: 'tail';
    };

const calculateSegments = (
  invoicesAndBenefits: InvoiceAndBenefits[],
  isPayAllFeatureEnabled: boolean
) => {
  const lastUnpaidInvoiceIndex = findLastIndex(
    invoicesAndBenefits,
    (x) =>
      !!x.invoice &&
      typeof x.invoice === 'object' &&
      x.invoice.type === INVOICE_TYPE_ISSUED &&
      isInvoicePayable(x.invoice)
  );

  const payableInvoicesCount = invoicesAndBenefits
    .map((x) => x.invoice)
    .filter((x) => !!x && typeof x === 'object' && isInvoicePayable(x)).length;

  return [
    ...invoicesAndBenefits.flatMap((x, index) => [
      { type: 'billing-cycle' as const, ...x, index },
      ...(index === lastUnpaidInvoiceIndex && payableInvoicesCount > 1 && isPayAllFeatureEnabled
        ? [{ type: 'pay-all' as const }]
        : []),
    ]),
    { type: 'tail' as const },
  ];
};

export const Timeline = (props: {
  segmentsPerYear: number;
  invoicesAndBenefits: InvoiceAndBenefits[];
  historicInvoices: Invoice[];
}) => {
  const { segmentsPerYear, invoicesAndBenefits, historicInvoices } = props;

  const { isEnabled: isPayAllFeatureEnabled } = usePayAllFeatureSwitcher();

  const segments = useMemo<Segment[]>(
    () => calculateSegments(invoicesAndBenefits, isPayAllFeatureEnabled),
    [invoicesAndBenefits, isPayAllFeatureEnabled]
  );
  const containerDivRef = useRef<HTMLDivElement | null>(null);

  const [scrollToIndex] = useState(getScrollToIndex(segments));
  useEffect(() => {
    if (!containerDivRef.current) return;
    const SEGMENT_WIDTH_PX = 136;
    const left = Math.max(Math.round(SEGMENT_WIDTH_PX * (scrollToIndex - 1.5)), 0);
    containerDivRef.current.scrollTo({
      left: left,
      behavior: 'auto',
    });
  }, [scrollToIndex]);

  return (
    <div className="timeline__container" ref={containerDivRef}>
      <TopLine
        segmentsPerYear={segmentsPerYear}
        segments={segments}
        historicInvoices={historicInvoices}
      />
      <MiddleLine segments={segments} historicInvoices={historicInvoices} />
      <BottomLine segments={segments} historicInvoices={historicInvoices} />
    </div>
  );
};
